import { NumberFormatter, Stack, Text, Title } from '@mantine/core';
import { InfoRow } from 'components/InfoRow';
import { Panel } from 'components/Panel';
import { i18nKeys } from 'locales';
import { useTranslation } from 'locales/logic';
import type { DebtorBalance } from 'shared/hooks/use-load-debtor-balance';
import { getCurrencySymbolFromCurrencyCode } from 'shared/utils/currency';
import type { CurrencyCode } from 'types/currency';

import { stripedStack } from '../PreferenceTab/TncPanel';
import { getBalanceColor } from './balance-utils';
import { computeDebtorBalance } from './ComputeDebtorBalance';

type BalanceSummaryPanelProps = {
  debtorBalance: DebtorBalance;
  currency: CurrencyCode;
  isLoading: boolean;
};

export const BalanceSummaryPanel = ({
  debtorBalance,
  currency,
  isLoading,
}: BalanceSummaryPanelProps) => {
  const { t } = useTranslation();
  const {
    remainingBalance,
    totalPaymentsSum,
    totalBilled,
    totalCreditNotesSum,
    lostAmount,
    totalLateFeesBilled,
    remainingLateFees,
  } = computeDebtorBalance(debtorBalance);

  return (
    <Panel
      pb={'space3'}
      withBorder
      isLoading={isLoading}
      mt="sm"
      title={t(i18nKeys.CLIENT.DETAIL.REMAIN_BALANCE)}
      topRight={
        <Title c={getBalanceColor(remainingBalance - remainingLateFees)} order={4}>
          <NumberFormatter
            suffix={` ${getCurrencySymbolFromCurrencyCode(currency)}`}
            decimalScale={2}
            prefix={remainingBalance - remainingLateFees < 0 ? '-' : ''}
            thousandSeparator=" "
            value={remainingBalance - remainingLateFees}
          />
        </Title>
      }
    >
      <Stack gap={0} className={stripedStack}>
        <InfoRow label={t(i18nKeys.AMOUNT_BILLED)} p={8}>
          <NumberFormatterCurrency currency={currency} value={totalBilled} />
        </InfoRow>
        <InfoRow label={t(i18nKeys.CLAIMED_LATE_FEES)} p={8}>
          <NumberFormatterCurrency currency={currency} value={totalLateFeesBilled} />
        </InfoRow>
        <InfoRow label={t(i18nKeys.NAV.CREDIT_NOTES)} p={8}>
          <NumberFormatterCurrency currency={currency} value={-totalCreditNotesSum} />
        </InfoRow>
        <InfoRow label={t(i18nKeys.BANK_ACCOUNT.RECONCILIATION.PAYMENTS)} p={8}>
          <NumberFormatterCurrency currency={currency} value={totalPaymentsSum} />
        </InfoRow>
        <InfoRow label={t(i18nKeys.LOST_SUM)} p={8}>
          <NumberFormatterCurrency currency={currency} value={-lostAmount} />
        </InfoRow>
        <InfoRow
          label={
            <Stack gap={0}>
              <Text span>{t(i18nKeys.CLIENT.DETAIL.REMAIN_BALANCE)}</Text>
              <Text span size="sm" c="gray.6">
                {t(i18nKeys.WITHOUT_FEES)}
              </Text>
            </Stack>
          }
          p={8}
        >
          <NumberFormatterCurrency
            value={remainingBalance - remainingLateFees}
            currency={currency}
          />
        </InfoRow>
        <InfoRow label={t(i18nKeys.REMAINING_LATE_FEES)} p={8}>
          <NumberFormatterCurrency currency={currency} value={remainingLateFees} />
        </InfoRow>
        <InfoRow
          label={
            <Stack gap={0}>
              <Text span>{t(i18nKeys.CLIENT.DETAIL.REMAIN_BALANCE)}</Text>
              <Text span size="sm" c="gray.6">
                {t(i18nKeys.WITH_FEES)}
              </Text>
            </Stack>
          }
          p={8}
        >
          <NumberFormatterCurrency value={remainingBalance} currency={currency} />
        </InfoRow>
      </Stack>
    </Panel>
  );
};

const NumberFormatterCurrency = ({
  currency,
  value,
}: {
  currency: CurrencyCode;
  value: number;
}) => {
  const isNegative = value < 0;
  return (
    <Text c="gray.10" fw={500}>
      <NumberFormatter
        allowNegative={false}
        prefix={isNegative ? '-' : ''}
        suffix={` ${getCurrencySymbolFromCurrencyCode(currency)}`}
        decimalScale={2}
        thousandSeparator=" "
        value={value}
      />
    </Text>
  );
};
